import {
    getLocale as getUserLocale,
    getLocaleOverride,
} from '@eventbrite/i18n';
import pickBy from 'lodash/pickBy';
import { TicketsPageSize } from '../../models/ticketsPage';
import { CheckoutState } from '../../state';
import { WithAppNamespace } from '../../state/app';

export { getUrgencySignals } from './getUrgencySignals';

export const getDefaultUserCountry = ({ app }: CheckoutState): string =>
    app.defaultUserCountry;

export const getEbDomain = ({ app }: CheckoutState) => app.ebDomain;

export const getActiveFFsFromCore = ({ app }: CheckoutState) => {
    return Object.keys(
        pickBy(app.featureFlags, (value: any) =>
            typeof value === 'object' ? value.isActive : !!value,
        ),
    ).sort();
};

export const getAffiliateCode = ({ app }: CheckoutState) => app.affiliateCode;

export const getFeatureFlags = ({ app }: CheckoutState) => ({
    featureFlags: app.featureFlags,
});

export const getIsModal = ({ app }: CheckoutState) => app.isModal;

export const getAppLocale = ({ app }: CheckoutState) => app.locale;
export const getLocales = ({ app }: CheckoutState) => app.locales;
export const getDefaultLocale = ({ app, event }: CheckoutState) =>
    getLocaleOverride() ??
    getUserLocale({
        eventLocale: ('properties' in event && event.properties.locale) || '',
        appLocale: app.locale,
    });

export const getLocaleProps = (state: CheckoutState) => ({
    locales: getLocales(state),
    defaultLocale: getDefaultLocale(state),
});

export const isEventbriteTLD = ({ app }: CheckoutState): boolean =>
    app.isEventbriteTLD;

export const getGooglePayEnvironment = ({ app }: CheckoutState) => {
    return app.isProd ? 'PRODUCTION' : 'TEST';
};

export const getCurrencyFormat = ({ app }: CheckoutState) => app.currencyFormat;

export const getIsRegEvent = ({ app }: CheckoutState) => app.isRegEvent;

export const getIsTicketSelectionCompactSizeFlow = ({ app }: CheckoutState) =>
    app.request?.params?.ticketsPageSize === TicketsPageSize.compact;

export const getEnablePaymentConstraintsInNewCreate = ({
    app,
}: CheckoutState) => !!app.featureFlags?.enablePaymentConstraintsInNewCreate;

export const getIsProd = ({ app }: CheckoutState) => !!app.isProd;

export const isIosApp = <State extends WithAppNamespace>({ app }: State) =>
    app.useIosProfile;

export const isAndroidApp = <State extends WithAppNamespace>({ app }: State) =>
    app.useAndroidProfile;

export const isNativeApp = <State extends WithAppNamespace>({ app }: State) =>
    !!(app.useIosProfile || app.useAndroidProfile);

export const hasFewTicketsLeftUrgencySignal = ({ app }: CheckoutState) =>
    !!app.urgencySignals?.messages.fewTickets;

export const hasSalesEndSoonUrgencySignal = ({ app }: CheckoutState) =>
    !!app.urgencySignals?.messages.salesEndSoon;

export const getCorrelationId = ({ app }: CheckoutState) =>
    app.request.correlation_id;

export const getSessionId = ({ app }: CheckoutState) => app.request.session_id;

export const getWaitlistCode = ({ app }: CheckoutState) => app.waitlistCode;

export const getWidgetParentUrl = ({ app }: CheckoutState) =>
    app.widgetParentUrl;

export const getInviteToken = ({ app }: CheckoutState) => app.inviteToken;

export const getCampaingToken = ({ app }: CheckoutState) => app.campaignToken;

export const getInviteEmail = ({ app }: CheckoutState) => app.inviteEmail;

export const getCountryWithPostalCode = ({ app }: CheckoutState) =>
    app.countryWithPostalCode;

export const isMyOrderFlow = ({ app }: CheckoutState) => !!app.isMyOrderFlow;

export const isPayinsFlow = ({ app }: CheckoutState) =>
    !!app.payins?.isPayinsFlow;

export const getExistingOrderId = ({ app }: CheckoutState) =>
    app.existingOrderId;

export const getIsMobile = ({ app }: CheckoutState) => app.isMobile;

export const getReloadAfterCheckout = ({ app }: CheckoutState) =>
    !!app.reloadAfterCheckout;
